import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form, FormControl, Button } from 'react-bootstrap';
import CustomBackground from '../../components/UI/CustomBackground/CustomBackground';
import { useTranslation, Translation } from 'react-i18next';
import graphQLClientInstance from '../../axios-graphql-client';
import MultiSelect from "react-multi-select-component";
import classes from './MyAlerts.module.css';
import Table, { tableOrigin } from '../../components/UI/Table/Table';
import { ReactComponent as InfoCircleLightIcon } from '../../assets/infoCircleLight.svg';
import { Formik } from "formik";
import * as Yup from "yup";
import { withRouter } from 'react-router-dom';
import Loader from '../../components/UI/Loader/Loader';
import {
  backgroundImages,
  overrideStrings,
  API_URLS,
  myAlertsReturnTypes,
  createAlertFromValues,
  autoCompletePostData,
  testDateAfter,
  getCountries,
  handleCountriesChange,
  COUNTRIES_SESSION_KEY,
  testDateFromMissing,
  compareResults,
  renderTooltipOverlay,
  BOOTSTRAP_CLASSES,
  testDateFutureDate
} from '../../shared/utility';
import CustomDatePicker, { calendarTomorrow } from '../../components/UI/CustomDatePicker/CustomDatePicker';
import SearchFormContainer from '../../components/UI/SearchFormContainer/SearchFormContainer';
import { useAlert } from '../../shared/AlertService';
import CustomAsyncTypehead from '../../components/UI/CustomAsyncTypehead/CustomAsyncTypehead';
import { ICountryOption } from '../SearchVarieties/SearchVarieties';

import { useMatomo } from '@datapunt/matomo-tracker-react';

const MyAlerts = (props: any) => {
  const { trackPageView } = useMatomo();

  const { t, i18n } = useTranslation();
  const [formValues, setFormValues] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [displayResults, setDisplayResults] = useState(true);
  const [countriesOptions, setCountriesOptions] = useState<ICountryOption[]>([]);
  const [data, setData] = useState<IAlertsResults[] | []>([]);
  const dataRef = React.useRef<any[]>();
  dataRef.current = data;

  const [sortBy, setSortBy] = useState([]);

  const alert = useAlert();

  const formInitialValues: { [key: string]: any } = {
    denomination: "",
    speciesLatinName: [],
    countries: [],
    testDate: [],
  };

  const [initialValues, setInitialValues] = useState<{ [key: string]: any }>(formInitialValues);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await getAlerts();
      setIsLoading(false);
    })()

    trackPageView({ documentTitle: 'My Alerts' });
  }, []);

  useEffect(() => {
    const sessionCountries = sessionStorage.getItem(COUNTRIES_SESSION_KEY);
    if (sessionCountries) {
      setCountriesOptions(JSON.parse(sessionCountries));
    } else {
      getCountries(setCountriesOptions);
    }
  }, []);

  const getAlerts = async () => {
    await graphQLClientInstance.get(API_URLS.SUBSCRIPTIONS)
      .then(res => {
        if (res.data?.subscriptions_list?.length > 0) {
          const responseData = res.data.subscriptions_list
            .map((alertRes: IAlertsResults) => {
              return {
                object_name: alertRes.object_name,
                countries: alertRes.conditions[0]?.['sub_countries'],
                species: alertRes.conditions[0]?.['sub_species'],
                data_start: alertRes.data_start,
                data_end: alertRes.data_end,
                id: alertRes.id
              }
            });

          if (sortBy?.length) {
            responseData.sort(compareResults(sortBy[0]))
          }

          setData(responseData);
        } else {
          alert({
            catchOnCancel: false,
            title: t("alert"),
            body: t("no_alerts"),
          })
        }
      })
      .catch(err => {
        console.log(err);
      })
  }

  const fetchData = React.useCallback(({ sortBy: tableSortBy }) => {
    setIsLoading(true);
    setSortBy(tableSortBy);
    const alertResultsArray = dataRef.current ? [...dataRef.current] : [];

    let sorting = { "id": myAlertsReturnTypes.ALERT_ID, desc: true };

    if (tableSortBy.length > 0) {
      sorting = tableSortBy[0];
    }

    alertResultsArray.sort(compareResults(sorting));

    setData(alertResultsArray);

    setIsLoading(false);
  }, [])

  const form = (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={Yup.object()
        .shape({
          denomination: Yup.string()
            .required(t("validation_errors.required")),
          testDate: Yup.array()
            .of(
              Yup.object().shape({
                type: Yup.mixed().oneOf(['from', 'to'] as const).required(),
                value: Yup.string().required()
              }))
            .required(t("validation_errors.required"))
            .test(
              "futureDate",
              t("error_messages.future_date"),
              testDateFutureDate
            )
            .test(
              "isDateAfter",
              t("error_messages.date_before"),
              testDateAfter
            )
            .test(
              "isDateFromMissing",
              t("error_messages.date_from_missing"),
              testDateFromMissing
            )
        })
      }

      onSubmit={(values: any, { setSubmitting, resetForm }) => {
        const payload = {
          ...values
        };
        setFormValues(payload);
        setSubmitting(false);
        setIsLoading(true);
        setDisplayResults(true);
        graphQLClientInstance.post(API_URLS.SUBSCRIPTIONS, createAlertFromValues(values))
          .then(async (res) => {
            if (!res.data?.err_message) {
              await getAlerts();
            } else if (res.data?.err_message?.includes("max_subscription_threshold_reached")) {
              alert({
                catchOnCancel: false,
                title: t("alert"),
                body: t("too_many_alerts"),
              })
            }
            setIsLoading(false);
          })
          .catch(err => {
            setIsLoading(false);
            console.log(err);
          })
          .finally(() => { resetForm() })
      }
      }
    >

      {formProps => (
        <Form
          className={classes.searchForm}
          onSubmit={(e) => {
            formProps.handleSubmit();
            e.preventDefault();
          }}>
          <Row className={classes.searchFieldsRow}>
            <Col md="4" id="denominationContainer">
              <Form.Group controlId="searchForm.denomination">
                <Form.Label className={["w-100", classes.required].join(" ")}>
                  {t("denomination")}
                  {renderTooltipOverlay(
                    t("tooltips_my_alerts.denomination"),
                    <InfoCircleLightIcon />
                  )}
                </Form.Label>
                <FormControl
                  className={formProps.errors.denomination ? BOOTSTRAP_CLASSES.IS_INVALID : ""}
                  name='denomination'
                  as="input"
                  type="text"
                  value={formProps.values.denomination}
                  onChange={formProps.handleChange}
                />
                <Form.Control.Feedback type="invalid">
                  {formProps.errors.denomination}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md="8" id="speciesLatinNameContainer">
              <Form.Group controlId="searchForm.speciesLatinName">
                <Form.Label className="w-100">
                  {t("species_latin_name")}
                  {renderTooltipOverlay(
                    t("tooltips_my_alerts.species_latin_name"),
                    <InfoCircleLightIcon />
                  )}
                </Form.Label>
                <CustomAsyncTypehead
                  className={formProps.errors.speciesLatinName ? BOOTSTRAP_CLASSES.IS_INVALID : ""}
                  isInvalid={formProps.errors.speciesLatinName ? true : false}
                  useCache={false}
                  selected={formProps.values.speciesLatinName}
                  newSelectionPrefix={t("typeahead_new_selection")}
                  name='speciesLatinName'
                  id='speciesLatinNameTypeahead'
                  label={t("species_latin_name")}
                  postUrl={API_URLS.GRAPHQL}
                  postData={autoCompletePostData}
                  selectFieldValue="exact"
                  includeSynonymsFieldValue={true}
                  autoCompleteName="autoCompleteSpeciesLatinName"
                  autoCompleteReturnString="{ name principal specieid }"
                  autoCompleteResponseFieldValue="specieid"
                  setFieldValue={formProps.setFieldValue}
                  multiple
                />
              </Form.Group>
            </Col>
            <Col md="4" id="countriesContainer">
              <Form.Group controlId="searchForm.countries">
                <Form.Label className="w-100">
                  {t("countries")}
                  {renderTooltipOverlay(
                    t("tooltips_my_alerts.countries"),
                    <InfoCircleLightIcon />
                  )}
                </Form.Label>
                <MultiSelect
                  onChange={(val: any) => { handleCountriesChange(val, formProps.values.countries, countriesOptions, formProps.setFieldValue, "countries"); }}
                  value={formProps.values.countries}
                  className="customMultiSelect"
                  options={countriesOptions}
                  disableSearch={true}
                  overrideStrings={overrideStrings()}
                  labelledBy="countries" />
              </Form.Group>
            </Col>
            <Col md="4" id="testDateContainer">
              <Form.Group>
                <Form.Label className="w-100">
                  {t("valid_date")}
                  {renderTooltipOverlay(
                    t("tooltips_my_alerts.valid_date"),
                    <InfoCircleLightIcon />
                  )}
                </Form.Label>
                <CustomDatePicker
                  minimumDate={calendarTomorrow()}
                  name="testDate"
                  controlId="searchForm.testDate"
                  setFieldValue={formProps.setFieldValue}
                  value={formProps.values.testDate}
                  invalid={formProps.errors.testDate && formProps.touched.testDate}
                />
                <Form.Control.Feedback type="invalid">
                  {formProps.errors.testDate}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row className={classes.actionsRow}>
            <Col md="12">
              <Button id="submitSearchForm" variant="primary" type="submit" disabled={formProps.isSubmitting} className="float-right">{t("create")}</Button>
              <Button
                id="clearFields"
                variant="link"
                onClick={() => {
                  setInitialValues(formInitialValues);
                  formProps.resetForm();
                }}
                className="float-right mr-2">
                  {t("clear_fields")}
              </Button>
              <Button id="help" variant="link" onClick={() => window.open(`help/${i18n.language}/My_alerts.html`, '_blank')} className="float-right mr-2">{t("help")}</Button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );

  const selectedColumns = [
    {
      name: myAlertsReturnTypes.DENOMINATION,
      label: <Translation>{(translate) => translate('denomination')}</Translation>,
      enabled: true,
      isDefault: true,
      defaultOrder: 0
    },
    {
      name: myAlertsReturnTypes.COUNTRY,
      label: <Translation>{(translate) => translate('country')}</Translation>,
      enabled: true,
      isDefault: true,
      disableSortBy: true,
      defaultOrder: 2
    },
    {
      name: myAlertsReturnTypes.SPECIES_CLASS_CODE,
      label: <Translation>{(translate) => translate('species_code')}</Translation>,
      enabled: true,
      isDefault: true,
      disableSortBy: true,
      defaultOrder: 3
    },
    {
      name: myAlertsReturnTypes.DATE_START,
      label: <Translation>{(translate) => translate('start_date')}</Translation>,
      enabled: true,
      isDefault: true,
      defaultOrder: 4,
    },
    {
      name: myAlertsReturnTypes.DATE_END,
      label: <Translation>{(translate) => translate('end_date')}</Translation>,
      enabled: true,
      isDefault: true,
      defaultOrder: 5,
    },
    {
      name: myAlertsReturnTypes.ALERT_ID,
      enabled: true,
      isDefault: true,
      disableSortBy: true,
      defaultOrder: 6,
    }
  ];

  const tableColumns = React.useMemo(() => {
    return [
      ...selectedColumns.map((item: any) => { return { ...item, Header: item.label, accessor: item.name } })
    ]
  }, [selectedColumns]);

  const deleteAlert = (alertId: any) => {
    if (alertId.toString().length) {
      alert({
        catchOnCancel: false,
        title: t("alert"),
        body: t("delete_alert_message"),
        alertVariant: "confirmation",
      })
        .then(() => {
          setIsLoading(true);
          graphQLClientInstance.delete(`${API_URLS.SUBSCRIPTIONS}/${alertId}`)
            .then(res => {
              if (res.data?.err_message?.includes("subscription_ids_deleted")) {
                if (dataRef.current) {
                  const updatedData = [...dataRef.current];
                  const index = updatedData.findIndex((item: any) => item.id === alertId)
                  updatedData.splice(index, 1);
                  //@ts-ignore
                  setData(updatedData);
                  dataRef.current = updatedData;
                } else if (res.data.response) {
                  alert({
                    catchOnCancel: false,
                    title: t("alert"),
                    body: t(`error_messages.${res}`),
                  })
                }
              }
            })
            .catch(err => {
              console.log(err);
            }).finally(() => setIsLoading(false));
        }
        )

    }
  }

  return (
    <>
      <Loader show={isLoading}></Loader>
      <CustomBackground image={backgroundImages.MY_TESTS}>
        <SearchFormContainer
          title={t("my_alerts.title")}
          subtitle={t("my_alerts.subtitle")}
        >
          {form}
        </SearchFormContainer>
      </CustomBackground>
      <Container fluid style={{ display: !(isLoading) ? 'block' : 'none' }} className={classes.resultsTable}>
        {displayResults ? (
          <Table
            columns={tableColumns}
            origin={tableOrigin.MY_ALERTS}
            hideColumnsButton={true}
            isSortable={true}
            data={data}
            fetchData={fetchData}
            deleteAlert={deleteAlert}
            hideDownloadExcelButton={true}
            formValues={formValues}
            showPagination={false}
            pageSizeOptions={[]}
          />
        ) : <></>}
      </Container>
    </>
  )
}

export default withRouter(MyAlerts);

export interface IAlertsResults {
  object_name: string,
  conditions: {
    sub_countries: string[],
    sub_species: string[]
  }[]
  data_start: Date,
  data_end: Date,
  id: string
}
